/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { FunctionComponent, useCallback, useMemo, useState } from 'react';
import { ICustomNodeProps } from './types';
import { NodeHeader } from './NodeHeader';
import { NodeBody } from './NodeBody';
import '../../../../resources/styles/theme.scss';
import { useDispatch } from 'react-redux';
import { closeDialog, openDialog } from '../../../../../app/dialog/dialog.actions';
import { deleteNode, updateNode } from '../../../../../app/page/page-storage/node/node.actions';
import { useSelect, useSelectWithParams } from '../../../../../utils/selector.utils';
import { authSelectors } from '../../../../../app/auth/auth.selectors';
import { nodeSelectors } from '../../../../../app/resource/node/node.selectors';
import { userSelectors } from '../../../../../app/resource/user/user.selectors';
import { IOrganizationChartNode } from '../../../../../app/resource/node/node.types';
import { ORG_CHART_NODE_DRAG_HANDLE_CLASS } from '../utils/reactFlowGraph';

export const CustomNode: FunctionComponent<ICustomNodeProps> = ({ nodeData, chartType, readOnly = false }) => {
  const user = useSelectWithParams(userSelectors.selectResourceById, nodeData.userId);
  const isAdmin = useSelect(authSelectors.selectUserIsAdmin);
  const editable = isAdmin && !readOnly;
  const dispatch = useDispatch();

  const openAddNodeFormDialog = useCallback(() => {
    dispatch(openDialog('nodeFormDialog', { parentId: nodeData.id, chartType }));
  }, [dispatch, nodeData.id, chartType]);

  const openEditNodeFormDialog = useCallback(() => {
    dispatch(openDialog('nodeFormDialog', { nodeData, chartType }));
  }, [dispatch, nodeData, chartType]);

  const [isActive, setActive] = useState(false);

  const toggleClass = () => {
    setActive(!isActive);
  };
  const nodes = useSelect(nodeSelectors.selectResourceList);
  const childNodes = useMemo(() => nodes.filter((n) => n.parentIds?.includes(String(nodeData.id))), [
    nodes,
    nodeData.id,
  ]);
  const onlyParentChildNodes = useMemo(() => childNodes.filter((child) => (child.parentIds?.length ?? 0) === 1), [
    childNodes,
  ]);
  const hasOnlyParentChildNodes = onlyParentChildNodes.length > 0;
  const close = useCallback(() => setActive(false), [setActive]);

  const onConfirmDeleteNode = useCallback(() => {
    dispatch(deleteNode(nodeData.id));
    dispatch(closeDialog('confirmation'));
  }, [dispatch, nodeData.id]);

  const onConfirmDeleteWithChildren = useCallback(
    (newParentIds: string[]) => {
      onlyParentChildNodes.forEach((child: IOrganizationChartNode) => {
        dispatch(
          updateNode(child.id, {
            id: child.id,
            nodeTitle: child.nodeTitle,
            description: child.description,
            color: child.color,
            parentIds: newParentIds.length ? newParentIds : null,
          }),
        );
      });
      dispatch(deleteNode(nodeData.id));
      dispatch(closeDialog('nodeDeleteDialog'));
    },
    [dispatch, nodeData.id, onlyParentChildNodes],
  );

  const openDeleteModal = useCallback(() => {
    if (hasOnlyParentChildNodes) {
      dispatch(
        openDialog('nodeDeleteDialog', {
          nodeId: nodeData.id,
          onConfirm: onConfirmDeleteWithChildren,
        }),
      );
    } else {
      dispatch(
        openDialog('confirmation', {
          title: 'Delete seat',
          message: 'Deleting a seat is permanent and cannot be undone. Do you still want to continue?',
          onConfirm: onConfirmDeleteNode,
        }),
      );
    }
  }, [dispatch, hasOnlyParentChildNodes, nodeData.id, onConfirmDeleteWithChildren, onConfirmDeleteNode]);

  const stopNodeGesture = useCallback((e: React.SyntheticEvent) => {
    e.stopPropagation();
  }, []);

  /** Prevents selection-on-drag / pane handlers from seeing the gesture (capture phase). */
  const stopNodeGestureCapture = useCallback((e: React.PointerEvent | React.MouseEvent) => {
    e.stopPropagation();
  }, []);

  const userData = user
    ? {
        firstName: user.firstName,
        lastName: user.lastName,
        image: user.image,
      }
    : nodeData.externalUser
    ? {
        firstName: nodeData.externalUser.firstName,
        lastName: nodeData.externalUser.lastName,
        image: nodeData.externalUser.image,
      }
    : null;

  return (
    <div className="orgchart-node-wrap" key={nodeData.id}>
      <div
        className={`orgchart-box ${ORG_CHART_NODE_DRAG_HANDLE_CLASS} element element${nodeData.id}`}
        style={{
          height: '290px',
          width: '200px',
          position: 'relative',
          zIndex: 1,
          backgroundColor: nodeData.color,
        }}
      >
        <div className="orgchart-box-header">
          <NodeHeader nodeData={nodeData} userData={userData} />
          {editable && (
            <div
              className={`orgchart-box-options dropdown nodrag nopan${isActive ? ' opened' : ''}`}
              onMouseDown={stopNodeGesture}
              onPointerDownCapture={stopNodeGestureCapture}
              onMouseDownCapture={stopNodeGestureCapture}
              onMouseLeave={close}
            >
              <div
                className="round-icon-smaller dropdown-button"
                style={{ backgroundColor: nodeData.color }}
                onClick={toggleClass}
              >
                <i className="icon-pencil white f-0" />
              </div>
              <ul className="dropdown-menu pointer">
                <li onClick={openAddNodeFormDialog}>
                  <span className="drop-item">Add Seat</span>
                </li>
                <li onClick={openEditNodeFormDialog}>
                  <span className="drop-item">Edit Seat</span>
                </li>
                <li onClick={openDeleteModal}>
                  <span className="drop-item">Delete Seat</span>
                </li>
              </ul>
            </div>
          )}
        </div>
        {nodeData.description && (
          <div className="orgchart-box-body">
            <div className="orgchart-body-list">
              <NodeBody description={nodeData.description} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
